import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SolicitudService } from '@services/solicitud.service';
import { TrazabilidadBitacora, ExpedienteDetalle } from '@models/evaluacion-medica.interface';

interface TrazabilidadEvento {
  fecha: string; // ISO
  usuario: string;
  tipo: string; // e.g., 'notificacion', 'cambio_estado'
  descripcion: string;
  detallesExtra?: string;
  estadoAnterior?: string;
  estadoNuevo?: string;
}

@Component({
  selector: 'app-trazabilidad',
  templateUrl: './trazabilidad.component.html',
  styleUrls: ['./trazabilidad.component.scss']
})
export class TrazabilidadComponent implements OnInit {
  public expedienteId?: string | null;
  public eventos: TrazabilidadEvento[] = [];
  public filtered: TrazabilidadEvento[] = [];
  public tipos: string[] = [];
  public filtroTipo = '';
  public isLoading = false;
  public expediente: ExpedienteDetalle | null = null;

  constructor(private route: ActivatedRoute, private solicitudService: SolicitudService) {}

  ngOnInit(): void {
    this.expedienteId = this.route.snapshot.paramMap.get('id');
    if (this.expedienteId) {
      this.loadTimeline(+this.expedienteId);
    }
  }

  private async loadTimeline(id: number) {
    this.isLoading = true;
    try {
      const [bitacora, details] = await Promise.all([
        this.solicitudService.obtenerTrazabilidad(id),
        this.solicitudService.obtenerDetalleEvalucionMedica(id)
      ]);

      this.expediente = details.expediente;
      
      this.eventos = bitacora.map(b => {
        const usuarioNombre = [b.usu_nombres, b.usu_apellido_paterno, b.usu_apellido_materno]
                              .filter(Boolean).join(' ').trim() || `Usuario ID ${b.bit_usuario_responsable}`;
        
        return {
          fecha: b.bit_fecha,
          usuario: usuarioNombre,
          tipo: b.bit_tipo_accion,
          descripcion: b.bit_accion,
          estadoAnterior: b.eex_estado_anterior_nombre,
          estadoNuevo: b.eex_estado_nuevo_nombre
        };
      });

      // Sort by date descending
      this.eventos.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
      
      this.tipos = Array.from(new Set(this.eventos.map(e => e.tipo)));
      this.applyFilter();
    } catch (error) {
      console.error('Error loading timeline', error);
      // Fallback/Demo data if needed? Or just empty.
      this.eventos = [];
      this.applyFilter();
    } finally {
      this.isLoading = false;
    }
  }

  applyFilter() {
    if (!this.filtroTipo) {
      this.filtered = [...this.eventos];
    } else {
      this.filtered = this.eventos.filter(e => e.tipo === this.filtroTipo);
    }
  }

  exportCSV() {
    const rows = [['Fecha','Usuario','Tipo','Descripción'], ...this.filtered.map(e => [e.fecha, e.usuario, e.tipo, e.descripcion])];
    const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g,'""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = `trazabilidad-${this.expedienteId ?? 'sin-id'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  exportPDF() {
    // Simple printable HTML - user can Save as PDF via print dialog
    const win = window.open('', '_blank', 'noopener');
    if (!win) { alert('El navegador bloqueó la apertura de la ventana. Permite popups para esta página.'); return; }
    const title = `Trazabilidad - ${this.expedienteId ?? ''}`;
    const rows = this.filtered.map(e => `<tr><td>${e.fecha}</td><td>${e.usuario}</td><td>${e.tipo}</td><td>${e.descripcion}</td></tr>`).join('');
    win.document.write(`
      <html>
      <head>
        <title>${title}</title>
        <style>table{width:100%;border-collapse:collapse}td,th{border:1px solid #ccc;padding:8px}</style>
      </head>
      <body>
        <h2>${title}</h2>
        <table>
          <thead><tr><th>Fecha</th><th>Usuario</th><th>Tipo</th><th>Descripción</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body>
      </html>
    `);
    win.document.close();
    win.focus();
    // give the window a moment then trigger print
    setTimeout(() => win.print(), 300);
  }
}
